import { FC, useState } from "react";
import { Button, Card, Space, Tag, Spin, Alert, Empty, Modal, Select, Form, Table, Switch } from "antd";
import { Calendar, Clock, Users, Route, User, Truck } from "lucide-react";
import { useGetTodayVesselAssignments, useGetVesselsForDropdown } from "~/services/vessel/vessel.queries";
import { VesselStaffRole } from "~/utils/constants";
import { formatToAMPM } from "~/utils";
import { formatStaffName } from "~/utils/format-staff-name";

const formatTime = (datetime: string | Date) => {
  if (!datetime) return "";
  return formatToAMPM(new Date(datetime));
};

export const Assignments: FC = () => {
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [form] = Form.useForm();
  
  const { 
    data: todayAssignments, 
    isLoading, 
    error 
  } = useGetTodayVesselAssignments();

  const { data: allVessels } = useGetVesselsForDropdown();

  const getStatusColor = (status?: string | null) => {
    switch (status) {
      case "in-progress":
        return "processing";
      case "scheduled":
        return "blue";
      case "completed":
        return "success";
      default:
        return "default";
    }
  };

  const getDrivers = (vesselStaffs: any[]) => {
    return vesselStaffs
      ?.filter(vs => vs.roles.includes(VesselStaffRole.DRIVER))
      ?.map(vs => formatStaffName(vs.staff))
      ?.join(", ") || "No drivers assigned";
  };

  const getEstimatedTime = (serviceTickets: any[]) => {
    if (!serviceTickets?.length) return "N/A";
    const totalDuration = serviceTickets.reduce((sum, ticket) => sum + (ticket.duration || 0), 0);
    return totalDuration > 0 ? `${totalDuration} hrs` : "N/A";
  };

  const getRoute = (serviceTickets: any[]) => {
    if (!serviceTickets?.length) return null;
    
    const firstTicket = serviceTickets[0];
    const lastTicket = serviceTickets[serviceTickets.length - 1];
    
    return {
      origin: firstTicket?.location?.addressName || "Unknown",
      destination: lastTicket?.location?.addressName || "Unknown"
    };
  };

  const vesselColumns = [
    {
      title: 'Vessel',
      dataIndex: 'vesselRef',
      key: 'vesselRef',
      render: (text: string, record: any) => (
        <div className="flex items-center">
          <Truck size={16} className="mr-2 text-blue-600" />
          <span className="font-medium">{text || record.name}</span>
        </div>
      ),
    },
    {
      title: 'Current Drivers',
      dataIndex: 'vesselStaffs',
      key: 'drivers',
      render: (vesselStaffs: any[]) => (
        <div>
          {vesselStaffs?.filter(vs => vs.roles.includes(VesselStaffRole.DRIVER)).length > 0 ? (
            vesselStaffs
              .filter(vs => vs.roles.includes(VesselStaffRole.DRIVER))
              .map(vs => {
                return (
                  <Tag key={vs.id} color="blue" className="mb-1">
                    {formatStaffName(vs.staff)}
                  </Tag>
                );
              })
          ) : (
            <span className="text-gray-400">No drivers assigned</span>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'vesselStatus',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status || 'Available'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, _record: any) => (
        <Button size="small" type="link">
          Manage Drivers
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Card
        title="Today's Assignments & Routes"
        className="rounded-lg border border-gray-200"
        headStyle={{ fontWeight: 500 }}
      >
        <div className="flex justify-center items-center py-8">
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        title="Today's Assignments & Routes"
        className="rounded-lg border border-gray-200"
        headStyle={{ fontWeight: 500 }}
      >
        <Alert
          message="Error loading assignments"
          description="Failed to load today's vessel assignments. Please try again."
          type="error"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Card
      title="Today's Assignments & Routes"
      className="rounded-lg border border-gray-200"
      headStyle={{ fontWeight: 500 }}
    >
      <Space direction="vertical" size="middle" className="w-full">
        {!todayAssignments?.length ? (
          <Empty 
            description="No assignments for today" 
            image={Empty.PRESENTED_IMAGE_SIMPLE} 
          />
        ) : (
          todayAssignments.map((assignment) => {
            const route = getRoute(assignment.serviceTickets);
            const drivers = getDrivers(assignment.vesselStaffs || []);
            const estimatedTime = getEstimatedTime(assignment.serviceTickets);
            
            return (
              <Card key={assignment.id} className="rounded-lg border border-gray-200">
                <div className="flex justify-between items-center">
                  <div className="font-medium">{assignment.vesselRef || assignment.name}</div>
                  <div className="flex items-center space-x-2">
                    <Tag 
                      color={getStatusColor(assignment.vesselStatus)} 
                      className="rounded-full px-2 py-0.5 text-xs"
                    >
                      {assignment.vesselStatus || "Available"}
                    </Tag>
                    {assignment.serviceTickets?.[0]?.client && (
                      <Tag color="blue" className="rounded-full px-2 py-0.5 text-xs">
                        {assignment.serviceTickets[0].client.companyName}
                      </Tag>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 text-sm">
                  <div>
                    <div className="text-gray-500 text-xs flex items-center mb-2">
                      <Route size={12} className="mr-1" />
                      Route
                    </div>
                    {route ? (
                      <>
                        <div className="flex items-center mt-2">
                          <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center text-xs">
                            A
                          </div>
                          <div className="flex-1 border-b border-dashed border-gray-300 mx-2" />
                          <div className="w-5 h-5 rounded-full bg-green-100 text-green-800 flex items-center justify-center text-xs">
                            B
                          </div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{route.origin}</span>
                          <span>{route.destination}</span>
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-400 text-xs">No route assigned</div>
                    )}
                  </div>
                  
                  <div>
                    <div className="text-gray-500 text-xs flex items-center mb-1">
                      <Users size={12} className="mr-1" />
                      Drivers
                    </div>
                    <div className="mt-1">{drivers}</div>
                    <div className="text-gray-500 text-xs mt-1 flex items-center">
                      <Clock size={12} className="mr-1" />
                      Estimated time: {estimatedTime}
                    </div>
                  </div>
                </div>
                
                {assignment.serviceTickets?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="text-gray-500 text-xs mb-2">Service Tickets ({assignment.serviceTickets.length})</div>
                    <div className="space-y-1">
                      {assignment.serviceTickets.slice(0, 2).map((ticket) => (
                        <div key={ticket.id} className="text-xs text-gray-600 flex items-center">
                          <Calendar size={10} className="mr-1" />
                          {ticket.serial} - {ticket.serviceType}
                          {ticket.serviceDatetime && (
                            <span className="ml-2 text-gray-400">
                              {formatTime(ticket.serviceDatetime)}
                            </span>
                          )}
                        </div>
                      ))}
                      {assignment.serviceTickets.length > 2 && (
                        <div className="text-xs text-blue-600">
                          +{assignment.serviceTickets.length - 2} more tickets
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Card>
            );
          })
        )}
        
        <Card className="rounded-lg border border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <div className="font-medium">Upcoming Assignments</div>
              <div className="text-sm text-gray-500 mt-1">
                Next scheduled assignments
              </div>
            </div>
            <Button size="small" className="border-gray-300 hover:bg-gray-50">
              View All
            </Button>
          </div>
        </Card>
        
        <Space size="middle" className="flex justify-between">
          <Button 
            className="border-gray-300 hover:bg-gray-50"
            onClick={() => setDriverModalOpen(true)}
          >
            Driver Assignment
          </Button>
          <Button 
            className="border-gray-300 hover:bg-gray-50"
            onClick={() => setRouteModalOpen(true)}
          >
            Route Planning
          </Button>
        </Space>
      </Space>

      {/* Driver Assignment Modal */}
      <Modal
        title={
          <div className="flex items-center">
            <User size={20} className="mr-2" />
            Driver Assignment
          </div>
        }
        open={driverModalOpen}
        onCancel={() => setDriverModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setDriverModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary">
            Save Changes
          </Button>,
        ]}
        width={800}
      >
        <div className="py-4">
          <p className="text-gray-600 mb-4">
            Manage driver assignments for all vessels. You can assign or remove drivers from each vessel.
          </p>
          
          <Table
            dataSource={allVessels || []}
            columns={vesselColumns}
            pagination={false}
            size="small"
            rowKey="id"
          />
        </div>
      </Modal>

      {/* Route Planning Modal */}
      <Modal
        title={
          <div className="flex items-center">
            <Route size={20} className="mr-2" />
            Route Planning
          </div>
        }
        open={routeModalOpen}
        onCancel={() => setRouteModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setRouteModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary">
            Save Routes
          </Button>,
        ]}
        width={1000}
      >
        <div className="py-4">
          <p className="text-gray-600 mb-4">
            Plan and optimize routes for vessel assignments. Configure pickup and delivery sequences.
          </p>
          
          <Form form={form} layout="vertical">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Form.Item label="Select Vessel" name="vesselId">
                  <Select
                    placeholder="Choose a vessel"
                    options={allVessels?.map(vessel => ({
                      label: `${vessel.vesselRef || vessel.name} - ${vessel.vesselType?.name}`,
                      value: vessel.id,
                    }))}
                  />
                </Form.Item>
                
                <Form.Item label="Route Optimization" name="optimization">
                  <Select
                    placeholder="Select optimization method"
                    options={[
                      { label: "Shortest Distance", value: "distance" },
                      { label: "Fastest Time", value: "time" },
                      { label: "Manual Order", value: "manual" },
                    ]}
                  />
                </Form.Item>
                
                <Form.Item label="Auto-assign Driver" name="autoAssign">
                  <Switch defaultChecked />
                </Form.Item>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium mb-3">Route Preview</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                    <span>Start: Warehouse Location</span>
                  </div>
                  <div className="border-l-2 border-dashed border-gray-300 ml-1.5 h-4"></div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                    <span>Stop 1: Client Location A</span>
                  </div>
                  <div className="border-l-2 border-dashed border-gray-300 ml-1.5 h-4"></div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
                    <span>Stop 2: Client Location B</span>
                  </div>
                  <div className="border-l-2 border-dashed border-gray-300 ml-1.5 h-4"></div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                    <span>End: Return to Warehouse</span>
                  </div>
                </div>
                
                <div className="mt-4 p-3 bg-white rounded border">
                  <div className="text-xs text-gray-500 mb-1">Estimated Stats</div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Total Distance:</span>
                      <span className="font-medium">45.2 miles</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Travel Time:</span>
                      <span className="font-medium">1.5 hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Service Time:</span>
                      <span className="font-medium">3.0 hours</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </Modal>
    </Card>
  );
};
